import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Pencil, Wand2, ShieldCheck } from "lucide-react";
import { api } from '../lib/api';

interface PromptManagerProps {
  token: string;
  userRole: 'user' | 'admin' | 'superadmin';
}

interface Prompt {
  id: number;
  name: string;
  template: string;
  variables?: Record<string, any> | null;
  isActive: boolean;
  isSystemDefined: boolean;
  userId: number | null;
}

/* ------------------------------------------------------------------
 * Tiny Tailwind Modal
 * ----------------------------------------------------------------*/
const Modal: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
};

const PromptManager: React.FC<PromptManagerProps> = ({ token, userRole }) => {
  const isSuper = userRole === 'superadmin';

  /* ------------------------------ state --------------------------------- */
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* modal draft */
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [variables, setVariables] = useState("{}");
  const [varsError, setVarsError] = useState<string | null>(null);
  const [isSystemDefinedDraft, setIsSystemDefinedDraft] = useState<boolean>(false);

  /* ---------------------------- helpers --------------------------------- */
  const resetModal = () => {
    setEditId(null);
    setName("");
    setTemplate("");
    setVariables("{}");
    setVarsError(null);
    setIsSystemDefinedDraft(false);
  };

  const parseVars = (txt: string) => {
    try {
      const obj = JSON.parse(txt || "{}");
      return typeof obj === "object" ? obj : null;
    } catch {
      return null;
    }
  };

  /* ----------------------------- data ----------------------------------- */
  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
  const { data } = await api.get(`/prompt/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrompts(data.prompts ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch prompts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  /* ------------------------- save (create / update) --------------------- */
  const savePrompt = async () => {
    const varsObj = parseVars(variables);
    if (varsObj === null) {
      setVarsError("Variables must be valid JSON");
      return;
    }

    setSaving(true);
    try {
      const payload: any = { name, template, variables: varsObj };
      if (isSuper) payload.isSystemDefined = isSystemDefinedDraft;

      if (editId === null) {
          await api.post(`/prompt`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
          await api.put(`/prompt/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setOpen(false);
      resetModal();
      fetchPrompts();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to save prompt");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------- handlers --------------------------------- */
  const handleNew = () => {
    resetModal();
    setOpen(true);
  };

  const handleEdit = (p: Prompt) => {
    setEditId(p.id);
    setName(p.name);
    setTemplate(p.template);
    setVariables(JSON.stringify(p.variables ?? {}, null, 2));
    setIsSystemDefinedDraft(!!p.isSystemDefined);
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this prompt?")) return;
    setSaving(true);
    try {
  await api.delete(`/prompt/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPrompts();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to delete prompt");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (id: number) => {
    setSaving(true);
    try {
  await api.put(`/prompt/${id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPrompts();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to activate prompt");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------------------ UI ------------------------------------ */
  return (
    <div className="max-w-5xl mx-auto p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold">Prompt Templates</h2>
        <button
          onClick={handleNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> New Template
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="border rounded divide-y">
          {prompts.map((p) => (
            <div key={p.id} className="p-4 flex items-start justify-between hover:bg-gray-50">
              <div className="space-y-1 w-3/5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 font-mono">ID: {p.id}</span>
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{p.name}</h3>
                  {p.isActive && (
                    <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">Active</span>
                  )}
                  {p.isSystemDefined && (
                    <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700 flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3" /> System
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">{p.template}</p>
              </div>

              <div className="flex gap-2 shrink-0">
                <button onClick={() => handleEdit(p)} className="p-2 rounded hover:bg-gray-100" title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-2 rounded hover:bg-gray-100" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
                {!p.isActive && (
                  <button onClick={() => handleActivate(p.id)} className="p-2 rounded hover:bg-gray-100" title="Activate">
                    <Wand2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {prompts.length === 0 && (
            <div className="p-6 text-gray-600">No prompts yet.</div>
          )}
        </div>
      )}

      <p className="text-sm text-gray-500">
        Activation is unique per scope:
        <span className="italic"> one active system-defined prompt (global) and one active per user (personal).</span>
      </p>

      {/* ----------------------- Modal content --------------------- */}
      <Modal
        title={editId ? "Edit Template" : "New Template"}
        open={open}
        onClose={() => {
          if (!saving) {
            setOpen(false);
            resetModal();
          }
        }}
      >
        {/* Name */}
        <div className="space-y-1">
          <label className="text-sm font-medium">Name</label>
          <input
            type="text"
            className="border rounded px-3 py-2 w-full"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
        </div>

        {/* Template with chips */}
        <div className="space-y-1">
          <label className="text-sm font-medium">Template</label>
          <textarea
            className="border rounded px-3 py-2 w-full h-32"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            disabled={saving}
            placeholder='Write a system or personal prompt. Context and user question are injected by code.'
          />
        </div>

        {/* Variables */}
        <div className="space-y-1">
          <label className="text-sm font-medium flex items-center gap-2">
            Variables (JSON)
            <span className="px-1 text-[10px] rounded bg-gray-200 text-gray-700">optional</span>
          </label>
          <textarea
            className="border rounded px-3 py-2 w-full h-24 font-mono"
            value={variables}
            onChange={(e) => setVariables(e.target.value)}
            disabled={saving}
            placeholder='{ "temperature": 0.2, "model": "gemini-1.5-pro" }'
          />
          {varsError && <p className="text-xs text-red-600">{varsError}</p>}
        </div>

        {/* System-defined toggle (superadmin only) */}
        {isSuper && (
          <div className="flex items-center gap-2 pt-2">
            <input
              id="sysdef"
              type="checkbox"
              className="h-4 w-4"
              checked={isSystemDefinedDraft}
              onChange={(e) => setIsSystemDefinedDraft(e.target.checked)}
              disabled={saving}
            />
            <label htmlFor="sysdef" className="text-sm">
              System defined (global)
            </label>
          </div>
        )}

        {/* Footer buttons */}
        <div className="pt-4 flex justify-end gap-2">
          <button
            className="px-3 py-1 rounded border"
            onClick={() => {
              if (!saving) {
                setOpen(false);
                resetModal();
              }
            }}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="px-4 py-1 rounded text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            onClick={savePrompt}
            disabled={saving || !name.trim() || !template.trim()}
          >
            {editId ? "Save" : "Create"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default PromptManager;
